import useSWRFetcher from "../commonly-stored/fetcher";
import useSWR from "swr";
import settings from "../../../constants/settings";
import { useCallback, useEffect, useState } from "react";
import useFetchAndTrace from "../fetchWithTrace";
import { ContainerAllocation } from "../../../reducers/componentSearch";
import { datetimeStringToUTCDatetimeString } from "../../valueFormatting";

interface ContainerState {
  container_barcode: string;
  bin_barcode: string | null;
  last_counted_amount: number;
  available_inventory_level: number;
  allocations: ContainerAllocation[];
}

export interface ContainerHistoryRecord {
  timestamp: string;
  user_id: string | null;
  part_id: string;
  previous_state: ContainerState;
  state: ContainerState;
}

export interface ContainerHistoryRecords {
  records: ContainerHistoryRecord[];
  page: number;
  starting_index: number;
  records_per_page: number;
  total_count: number;
}

const NUM_RECORDS_FOR_SUMMARY = 10;
const NUM_RECORDS_PER_ALL_HISTORY = 50;

function getHistoryUrl(
  page: number,
  numRecordsPerPage: number,
  searchTerm?: string,
  startDatetime?: string,
  endDatetime?: string,
  filterNew?: boolean,
) {
  const searchTermQuery = searchTerm ? `&search_term=${searchTerm}` : "";
  const startDateQuery = startDatetime
    ? `&start_date=${datetimeStringToUTCDatetimeString(startDatetime)}`
    : "";
  const endDateQuery = endDatetime
    ? `&end_date=${datetimeStringToUTCDatetimeString(endDatetime)}`
    : "";
  const updateTypeQuery = filterNew ? `&update_type=created` : "";
  return `${settings.api_url}/inventory/containers/history?num_per_page=${numRecordsPerPage}&page=${page}${searchTermQuery}${startDateQuery}${endDateQuery}${updateTypeQuery}`;
}

export default function useContainerHistorySummary() {
  const fetcher = useSWRFetcher<ContainerHistoryRecords>();

  const { data: containerHistory, mutate } = useSWR<ContainerHistoryRecords>(
    getHistoryUrl(1, NUM_RECORDS_FOR_SUMMARY),
    fetcher,
  );

  return {
    containerHistory: containerHistory || null,
    fetchHistory: () => void mutate(),
  };
}

export function usePaginatedContainerHistory(defaultSearchTerm?: string) {
  const fetchAndTrace = useFetchAndTrace();
  const [page, setPage] = useState(1);
  const [containerHistory, setContainerHistory] =
    useState<ContainerHistoryRecords | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm || "");
  const [startDatetime, setStartDatetime] = useState("");
  const [endDatetime, setEndDatetime] = useState("");
  const [filterNew, setFilterNew] = useState<boolean>(false);

  const historyRecordHandler = useCallback(async (response: Response) => {
    setLoading(false);
    if (response.ok) {
      const historyResponse =
        (await response.json()) as ContainerHistoryRecords;
      setContainerHistory(historyResponse);
    } else {
      throw Error("Failed to get container history");
    }
  }, []);

  const searchContainerHistory = useCallback(() => {
    setLoading(true);
    void fetchAndTrace(
      getHistoryUrl(
        page,
        NUM_RECORDS_PER_ALL_HISTORY,
        searchTerm,
        startDatetime,
        endDatetime,
        filterNew,
      ),
      historyRecordHandler,
      null,
    );
  }, [
    fetchAndTrace,
    historyRecordHandler,
    page,
    searchTerm,
    startDatetime,
    endDatetime,
    filterNew,
  ]);

  useEffect(() => {
    searchContainerHistory();
  }, [searchContainerHistory, defaultSearchTerm]);

  const resetSearch = () => {
    setSearchTerm(defaultSearchTerm || "");
    setStartDatetime("");
    setStartDatetime("");
    setFilterNew(false);
    setPage(1);
  };

  return {
    loading,
    containerHistory,
    searchTerm,
    setSearchTerm,
    setPage,
    resetSearch,
    page,
    setStartDatetime,
    setEndDatetime,
    setFilterNew,
  };
}
